<template>
    <div>
        <div v-if="records.length > 0">
            <div v-if="enableFilter">
                <record-filter-bar :id="id"
                                   :records="this.records"
                                   :record-fields-to-filter="this.recordFieldsToDisplay">
                </record-filter-bar>
            </div>

            <div v-if="sortedRecords.length > 0">
                <table class="table table-bordered table-striped table-sm">
                    <thead>
                        <tr class="thead-dark">
                            <th v-if="showViewButton" class="text-center cell-sm">
                                <i class="fa fa-eye" :title="'View ' + recordType | injectWhitespace"></i>
                            </th>

                            <th v-if="showEditButton" class="text-center cell-sm">
                                <i class="fa fa-pencil" :title="translate('RecordManager.' + recordType + '.edit_' + recordType)"></i>
                            </th>

                            <th v-for="(fieldName, index) in recordFieldsToDisplay" :class="[sortField == fieldName ? 'active-sort' : '']" @click="setSortFieldAndDirection(fieldName)" :key="index">
                                <span class="sort-records">
                                    <i v-if="sortField == fieldName && !reverseSort" class="fa fa-sort-asc active-sort"></i>
                                    <i v-else-if="sortField == fieldName && reverseSort" class="fa fa-sort-desc active-sort"></i>
                                    <i v-else class="fa fa-sort"></i>
                                </span>
                                {{ translate('RecordManager.' + recordType + '.' + fieldName) }}
                            </th>

                            <th v-if="showDeleteButton" class="text-center">
                                <i class="fas fa-trash-alt" :title="translate('RecordManager.' + recordType + '.delete_' + recordType)"></i>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(record, recordIndex) in sortedRecords" :key="recordIndex">
                            <td v-if="showViewButton" class="text-center cell-sm">
                                <a :href="viewRecordUri(record.id)" class="btn btn-secondary btn-sm" :title="'View ' + recordType | injectWhitespace">
                                    <i class="fas fa-eye"></i>
                                </a>
                            </td>

                            <td v-if="showEditButton" class="text-center cell-sm">
                                <a :href="editRecordUri(record.id)" class="btn btn-secondary btn-sm" :title="translate('RecordManager.' + recordType + '.edit_' + recordType)">
                                    <i class="fas fa-edit"></i>
                                </a>
                            </td>

                            <td v-for="(fieldName, fieldIndex) in recordFieldsToDisplay" :key="fieldIndex">
                                <template v-if="typeof record[fieldName] === 'object' && record[fieldName] !== null">
                                    <a v-if="record[fieldName].hasOwnProperty('url')"
                                       :href="record[fieldName]['url']">
                                      <span v-html="record[fieldName]['label']"></span>
                                    </a>
                                    <span v-else
                                          v-html="translate('RecordManager.' + recordType + '.' + record[fieldIndex]['label']) !== recordType + '.' + record[fieldIndex]['label'] ? translate('RecordManager.' + recordType + '.' + record[fieldIndex]['label']) : record[fieldIndex]['label']">
                                    </span>
                                </template>
                                <span v-else v-html="translate('RecordManager.' + recordType + '.' + record[fieldName]) !== recordType + '.' + record[fieldName] ? translate('RecordManager.' + recordType + '.' + record[fieldName]) : record[fieldName]">
                                </span>
                            </td>

                            <td v-if="showDeleteButton" class="text-center">
                                <delete-record-button :endpoint="deleteRecordUri(record.id)"
                                                      :redirect-url="viewRecordsUri()"
                                                      :title="translate('RecordManager.' + recordType + '.delete_' + recordType)"
                                                      :show-confirmation-dialog="true"
                                                      :confirmation-dialog-title="translate('RecordManager.' + recordType + '.delete_' + recordType)"
                                                      :confirmation-dialog-message="translate('RecordManager.' + recordType + '.delete_' + recordType + '_confirm')">
                                    <i class="fas fa-trash-alt"></i>
                                </delete-record-button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p v-else class="alert alert-info">
                No records matched your filter text: <strong>{{ this.filterQuery }}</strong>
            </p>
        </div>
        <p v-else>
            <slot name="no-records-message">
                {{ translate('RecordManager.' + recordType + '.no_' + recordType) }}
            </slot>
        </p>
    </div>
</template>

<script>
    export default {
        props: {
            records: { type: Array, required: true },
            recordType: { type: String, required: true },
            resourceUriPrefix: { type: String, required: false, default: null },
            recordFieldsToDisplay: { type: Array, required: false },
            enableFilter: { type: Boolean, required: false, default: true },
            showViewButton: { type: Boolean, required: false, default: false },
            showEditButton: { type: Boolean, required: false, default: true },
            showDeleteButton: { type: Boolean, required: false, default: true },
            defaultSortField: { type: String, required: false, default: null },
            defaultSortDirection: { type: String, required: false, default: 'asc' }
        },

        data() {
            return {
                id: this._uid,
                filterQuery: '',
                filteredRecords: this.records,
                sortField: this.defaultSortField,
                reverseSort: this.defaultSortDirection !== 'asc'
            }
        },

        computed: {
            sortedRecords() {
                // set the sort field and sort direction
                let _this = this;
                function compare(a, b) {
                    let sortField1 = a[_this.sortField];
                    let sortField2 = b[_this.sortField];

                    if (typeof a[_this.sortField] === 'object' && a[_this.sortField] !== null) {
                        if (a[_this.sortField].hasOwnProperty('url')) {
                            sortField1 = a[_this.sortField]['label'];
                            sortField2 = b[_this.sortField]['label'];
                        }
                    }

                    if (sortField1 < sortField2) {
                        return _this.reverseSort ? 1 : -1;
                    }
                    if (sortField1 > sortField2) {
                        return _this.reverseSort ? -1 : 1;
                    }
                    return 0;
                }
                return this.filteredRecords.sort(compare);
            } 
        },

        created() {
            EventDispatcher.listen('filterRecordsByFilterQuery' + this.id, (payload) => {
                this.filterQuery = payload.filterQuery;
                this.filteredRecords = payload.filteredRecords;
            });
        },

        methods: {
            setSortFieldAndDirection(fieldName) {
                this.reverseSort = fieldName == this.sortField ? !this.reverseSort : false;
                this.sortField = fieldName; 
            },

            viewRecordUri(recordId) {
            	return this.resourceUriPrefix + '/' + recordId;
            },

            editRecordUri(recordId) {
            	return this.resourceUriPrefix + '/' + recordId + '/edit';
            },

            deleteRecordUri(recordId) {
            	return this.resourceUriPrefix + '/' + recordId;
            },

            viewRecordsUri() {
            	return this.resourceUriPrefix;
            }
        }
    }
</script>

<style lang="scss" scoped>
    .table-bordered {
        thead th {
            white-space: nowrap !important;
            padding-right: 10px;
            cursor: pointer;
            /* prevent selecting text on double click when sorting table */
            -webkit-user-select: none; /* Chrome/Safari */        
            -moz-user-select: none; /* Firefox */
            -ms-user-select: none; /* IE10+ */
            &.active-sort {
                background-color: #777;
                color: white;
                font-weight: normal;
            }
            .sort-records {
                padding-right: 5px;
                i.active-sort {
                    color: #fff;
                }
            }
        }

        td {
            border: 1px solid #ddd;
        }
    }
    
    .cell-sm {
        width: 5%;
    }
</style>